import type { BooleanKind } from '@anytracker/core'

import styles from './BooleanInput.module.css'
import type { ValueInputProps } from './types.js'
import { useRadioGroupKeys } from './useRadioGroupKeys.js'

interface Props extends ValueInputProps {
  readonly kind: BooleanKind
}

/**
 * A two-state choice showing the track's own labels.
 *
 * Never "true"/"false" — the track author wrote "Did it"/"Skipped" or
 * "Yes"/"No" for a reason, and a control that ignores them makes every boolean
 * track read identically.
 *
 * Built as a radio group rather than a switch. A switch implies a default
 * on/off state; this has three, the third being *unanswered*, and re-pressing
 * the selected option returns to it.
 *
 * **Both states use the accent when chosen, not green and red.** The prototype
 * draws "yes" in the positive family and "no" in the caution one, which reads
 * beautifully for its example data — every boolean it ships is a track where
 * doing the thing is good. anytracker does not know that. A track called
 * "Headache" or "Panic attack" would light up green for the worst day of
 * someone's month, and nothing in the domain model could tell it not to
 * (see `docs/design-issues.md`). The two cards differ by the labels the track
 * author wrote and by a tick against a dash, which are marks for *yes and no*
 * rather than for good and bad.
 */
export function BooleanInput({
  kind,
  value,
  onChange,
  disabled = false,
  labelledBy,
  describedBy,
  variant = 'compact',
}: Props) {
  const current = typeof value === 'boolean' ? value : null

  const options = [
    { state: true, label: kind.trueLabel, icon: <Tick /> },
    { state: false, label: kind.falseLabel, icon: <Dash /> },
  ]

  const selectedIndex = options.findIndex((option) => option.state === current)
  const keys = useRadioGroupKeys(options.length, selectedIndex, (index) => {
    const option = options[index]
    if (option) {
      onChange(option.state)
    }
  })

  return (
    <div
      className={`${styles['root']} ${variant === 'full' ? styles['full'] : ''}`}
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
    >
      {options.map(({ state, label, icon }, index) => {
        const selected = current === state
        return (
          <button
            key={String(state)}
            ref={keys.register(index)}
            type="button"
            onKeyDown={keys.onKeyDown}
            role="radio"
            aria-checked={selected}
            tabIndex={keys.tabIndexFor(index)}
            disabled={disabled}
            className={`${styles['option']} ${selected ? styles['selected'] : ''}`}
            // Pressing the selected option clears it. Without this, a mis-tap
            // is unrecoverable without abandoning the entry, and the user
            // learns to record a wrong answer rather than no answer.
            onClick={() => onChange(selected ? null : state)}
          >
            {variant === 'full' && icon}
            <span>{label}</span>
          </button>
        )
      })}
    </div>
  )
}

/* Yes and no, not good and bad: a tick against a dash. */
function Tick() {
  return (
    <svg viewBox="0 0 24 24" width="40" height="40" fill="none" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M8 12.5l2.5 2.5L16 9.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Dash() {
  return (
    <svg viewBox="0 0 24 24" width="40" height="40" fill="none" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M8.5 12h7"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  )
}
